import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
  AfterViewInit,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  BasinStyle,
  BathroomConfigurationState,
  ColumnConfiguration,
  ConfigurationSection,
  HandleType,
  RowConfiguration,
  SelectedElement,
  UISelectionState,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import { BathroomConfigurationService } from 'src/app/services/configurators-2d/mobile/bathroom/bathroom-configuration.service';
import { captureBathroomPreviewElement } from 'src/app/services/configurators-2d/mobile/bathroom/bathroom-preview-capture.util';
import { faChevronLeft, faList, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { BasinType } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { ProductAttribute } from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { Location } from '@angular/common';
import { HasUnsavedChanges } from 'src/app/services/interfaces/has-unsaved-changes.interface';

@Component({
  selector: 'ng-casto-bathroom-configuration',
  templateUrl: './bathroom-configuration.component.html',
  styleUrls: ['./bathroom-configuration.component.scss'],
})
export class BathroomConfigurationComponent implements OnInit, OnDestroy, AfterViewInit, HasUnsavedChanges {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('accordionZone') accordionZone!: ElementRef<HTMLDivElement>;
  @ViewChild('renderZone') renderZone!: ElementRef<HTMLDivElement>;
  @Input() embedded: boolean = false;
  @Input() layoutMode: 'mobile' | 'desktop' = 'mobile';
  @Input() compositionIdInput: string | null = null;
  @Input() hideRender: boolean = false;
  @Output() configurationValidated = new EventEmitter<BathroomConfigurationState>();
  @Output() backRequested = new EventEmitter<void>();

  private _location = inject(Location);
  private _elementRef = inject(ElementRef);

  public listFa = faList;

  // État
  configuration: BathroomConfigurationState | null = null;
  uiSelection: UISelectionState | null = null;
  compositionId: string | null = null;
  isLoading = false;
  isValidating = false;

  // Scroll vers élément sélectionné
  private pendingScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastExternalSelectionKey: string | null = null;
  // Constantes pour le template
  readonly HandleType = HandleType;
  readonly sections: ConfigurationSection[] = ['dimensions', 'basin', 'finitions', 'handles', 'positions'];

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faMinus = faMinus;
  protected readonly faPlus = faPlus;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly configurationService: BathroomConfigurationService
  ) { }

  hasUnsavedChanges(): boolean {
    return this.configurationService.hasUnsavedChanges();
  }

  ngAfterViewInit(): void {
    this.configurationService.resetConfigurationPurchaseList();
  }

  ngOnInit(): void {
    this.compositionId = this.compositionIdInput ?? this.route.snapshot.paramMap.get('compositionId');

    // S'abonner aux changements
    combineLatest([this.configurationService.configuration$, this.configurationService.uiSelection$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([config, ui]) => {
        this.configuration = config;
        this.uiSelection = ui;
        this.isLoading = config.isLoading;
        this.scheduleScrollFromExternalPreview(ui);
      });

    // Charger la composition uniquement si elle n'est pas déjà chargée
    if (this.compositionId) {
      const currentConfig = this.configurationService.getCurrentConfiguration();
      const isAlreadyLoaded = currentConfig.composition?.id === this.compositionId;

      if (!isAlreadyLoaded) {
        this.loadComposition();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pendingScrollTimeout) clearTimeout(this.pendingScrollTimeout);
  }

  private async loadComposition(): Promise<void> {
    if (!this.compositionId) return;

    try {
      await this.configurationService.loadComposition(this.compositionId);
    } catch (error) {
      console.error('Erreur lors du chargement de la composition', error);
      if (this.embedded) {
        this.backRequested.emit();
        return;
      }
      this.router.navigate(['/salle-de-bain-mobile/compositions']);
    }
  }

  // ============================================
  // Navigation
  // ============================================

  async goBack(): Promise<void> {
    if (this.embedded) {
      this.backRequested.emit();
      return;
    }

    this._location.back();
  }

  async openSummary(): Promise<void> {
    if (!this.configuration) return;

    this.configuration.isValid = true; // TODO : à modifier

    if (this.configuration.isValid) {
      this.isValidating = true;
      try {
        const capturedFromVisiblePreview = await this.configurationService.captureVisiblePreviewImage();
        if (!capturedFromVisiblePreview) {
          await this.captureRenderAsImage();
        }
        if (this.embedded) {
          this.configurationValidated.emit(this.configuration);
          return;
        }
        await this.configurationService.sendToPurchaseList();
      } finally {
        this.isValidating = false;
      }
    }
  }

  private async captureRenderAsImage(): Promise<void> {
    const imageData = await this.captureRenderAsImageData();
    if (imageData) {
      this.configurationService.bathroomConfigurationSceneImage = imageData;
    }
  }

  private async captureRenderAsImageData(): Promise<string | null> {
    if (!this.renderZone) return null;

    return await captureBathroomPreviewElement(
      this.renderZone.nativeElement,
      "Erreur lors de la capture de l'image"
    );
  }

  // ============================================
  // Gestion des sections
  // ============================================

  setActiveSection(section: ConfigurationSection): void {
    this.configurationService.setActiveSection(section);
  }

  getSectionLabel(section: ConfigurationSection): string {
    const labels: Record<ConfigurationSection, string> = {
      dimensions: 'Dimensions',
      basin: 'Vasque',
      finitions: 'Finitions',
      handles: 'Poignées',
      positions: 'Positions',
    };
    return labels[section];
  }

  // ============================================
  // Gestion des sélections UI
  // ============================================

  onElementClick(element: SelectedElement): void {
    this.configurationService.selectElement(element);

    // Changer automatiquement de section selon l'élément
    if (element.type === 'column' || element.type === 'cover') {
      this.configurationService.setActiveSection('finitions');
      // Attendre la fin de l'animation d'ouverture de l'accordéon (300ms) avant de scroller
      if (this.pendingScrollTimeout) clearTimeout(this.pendingScrollTimeout);
      this.pendingScrollTimeout = setTimeout(() => {
        this.scrollToFinitionSection(element);
        this.pendingScrollTimeout = null;
      }, 350);
    } else if (element.type === 'basin') {
      this.configurationService.setActiveSection('basin');
    } else if (element.type === 'handle') {
      this.configurationService.setActiveSection('handles');
      if (this.pendingScrollTimeout) clearTimeout(this.pendingScrollTimeout);
      this.pendingScrollTimeout = setTimeout(() => {
        this.scrollToSection('handle-finition-section') || this.scrollToSection('handles-accordion-item');
        this.pendingScrollTimeout = null;
      }, 350);
    }
  }

  /**
   * Génère l'ID de section pour un élément de finition
   */
  getFinitionSectionId(element: SelectedElement): string {
    if (element.type === 'cover') {
      return `finition-cover-${element.rowIndex}`;
    } else if (element.type === 'column') {
      return `finition-column-${element.rowIndex}-${element.columnIndex}`;
    }
    return '';
  }

  /**
   * Scroll vers la section de finition correspondante à l'élément
   */
  private scrollToFinitionSection(element: SelectedElement): void {
    const sectionId = this.getFinitionSectionId(element);
    if (sectionId) this.scrollToSection(sectionId);
  }

  private scheduleScrollFromExternalPreview(ui: UISelectionState): void {
    if (!this.hideRender || !ui.selectedElement) return;

    const selectionKey = this.getSelectionScrollKey(ui.selectedElement, ui.activeSection);
    if (selectionKey === this.lastExternalSelectionKey) return;

    this.lastExternalSelectionKey = selectionKey;
    if (this.pendingScrollTimeout) clearTimeout(this.pendingScrollTimeout);
    this.pendingScrollTimeout = setTimeout(() => {
      this.scrollToSelectedElement(ui.selectedElement);
      this.pendingScrollTimeout = null;
    }, 350);
  }

  private getSelectionScrollKey(element: SelectedElement, activeSection: ConfigurationSection): string {
    const columnIndex = element.type === 'column' || element.type === 'handle' ? element.columnIndex : '';
    return `${activeSection}-${element.type}-${element.rowIndex ?? ''}-${columnIndex}`;
  }

  private scrollToSelectedElement(element: SelectedElement): void {
    if (element.type === 'column' || element.type === 'cover') {
      this.scrollToFinitionSection(element);
      return;
    }

    if (element.type === 'basin') {
      this.scrollToSection('basin-accordion-item');
      return;
    }

    if (element.type === 'handle') {
      this.scrollToSection('handle-finition-section') || this.scrollToSection('handles-accordion-item');
    }
  }

  /**
   * Scroll vers un élément du DOM par son ID. Retourne true si l'élément a été trouvé.
   */
  private scrollToSection(sectionId: string): boolean {
    const hostElement = this._elementRef.nativeElement as HTMLElement;
    const sectionElement = hostElement.querySelector<HTMLElement>(`#${sectionId}`);
    if (!sectionElement) return false;

    sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    return true;
  }

  isElementSelected(element: SelectedElement): boolean {
    const selected = this.uiSelection?.selectedElement;
    if (!selected) return false;

    if (selected.type !== element.type) return false;

    if (element.type === 'column' && selected.type === 'column') {
      return selected.rowIndex === element.rowIndex && selected.columnIndex === element.columnIndex;
    }

    if (element.type === 'cover' && selected.type === 'cover') {
      return selected.rowIndex === element.rowIndex;
    }

    return false;
  }

  // ============================================
  // Actions de configuration - Paramètres généraux
  // ============================================

  onBasinTypeChange(basinType: BasinType): void {
    this.configurationService.setBasinType(basinType);
  }

  onBasinStyleChange(basinStyle: BasinStyle): void {
    this.configurationService.setBasinStyle(basinStyle);
  }

  onRowOffsetChange(rowIndex: number, offset: number): void {
    this.configurationService.setRowOffset(rowIndex, offset);
  }

  onRowOffsetInputChange(rowIndex: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const rowConfig = this.configuration?.rowConfigurations.find(r => r.rowIndex === rowIndex);
    if (!rowConfig) return;

    let offset = parseInt(input.value, 10);

    if (isNaN(offset)) {
      input.value = String(rowConfig.offset);
      return;
    }

    offset = Math.max(rowConfig.minOffset, Math.min(rowConfig.maxOffset, offset));
    input.value = String(offset);
    this.configurationService.setRowOffset(rowIndex, offset);
  }

  incrementOffset(rowIndex: number, currentOffset: number): void {
    this.configurationService.setRowOffset(rowIndex, currentOffset + 1);
  }

  decrementOffset(rowIndex: number, currentOffset: number): void {
    this.configurationService.setRowOffset(rowIndex, currentOffset - 1);
  }

  onColumnWidthChange(rowIndex: number, columnIndex: number, width: number): void {
    this.configurationService.setColumnWidth(rowIndex, columnIndex, width);
  }

  onGlobalWidthChange(width: number): void {
    this.configurationService.setGlobalWidth(width);
  }

  onRowWidthChange(rowIndex: number, width: number): void {
    this.configurationService.setRowWidth(rowIndex, width);
  }

  // ============================================
  // Actions de configuration - Finitions
  // ============================================

  onColumnFinitionChange(rowIndex: number, columnIndex: number, finitionId: string): void {
    this.configurationService.setColumnFinition(rowIndex, columnIndex, finitionId);
    // Désélectionner l'élément pour voir le changement sur le rendu
    this.configurationService.selectElement(null);
  }

  onCoverFinitionChange(rowIndex: number, finitionId: string): void {
    this.configurationService.setCoverFinition(rowIndex, finitionId);
    // Désélectionner l'élément pour voir le changement sur le rendu
    this.configurationService.selectElement(null);
  }

  // ============================================
  // Actions de configuration - Appliquer à tous
  // ============================================

  /**
   * Vérifie si la finition de colonne peut être appliquée à tous les éléments
   */
  canApplyColumnFinitionToAll(finitionId: string): boolean {
    return this.configurationService.canApplyColumnFinitionToAll(finitionId);
  }

  /**
   * Applique la finition de colonne à tous les éléments compatibles
   */
  onApplyColumnFinitionToAll(finitionId: string): void {
    this.configurationService.applyColumnFinitionToAll(finitionId);
    this.configurationService.selectElement(null);
  }

  /**
   * Vérifie si la finition de cover peut être appliquée à tous les covers
   */
  canApplyCoverFinitionToAll(finitionId: string): boolean {
    return this.configurationService.canApplyCoverFinitionToAll(finitionId);
  }

  /**
   * Applique la finition de cover à tous les covers compatibles
   */
  onApplyCoverFinitionToAll(finitionId: string): void {
    this.configurationService.applyCoverFinitionToAll(finitionId);
    this.configurationService.selectElement(null);
  }

  // ============================================
  // Actions de configuration - Poignées
  // ============================================

  onHandleTypeChange(handleType: HandleType): void {
    this.configurationService.setHandleType(handleType);
  }

  onHandleLengthChange(length: number): void {
    this.configurationService.setHandleLength(length);
  }

  onHandleFinitionChange(finitionId: string): void {
    this.configurationService.setHandleFinition(finitionId);
    // Désélectionner l'élément pour voir le changement sur le rendu
    this.configurationService.selectElement(null);
  }

  // ============================================
  // Helpers pour le template
  // ============================================

  isOptionAvailable(options: Array<{ value: any; available: boolean }> | undefined, value: any): boolean {
    return options?.find(opt => opt.value === value)?.available ?? true;
  }

  getOptionReason(options: Array<{ value: any; reason?: string }> | undefined, value: any): string | undefined {
    return options?.find(opt => opt.value === value)?.reason;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  }

  trackByRowIndex(index: number, item: any): number {
    return item.rowIndex;
  }

  trackByColumnIndex(index: number, item: any): number {
    return item.columnIndex;
  }

  trackByValue(index: number, item: any): any {
    return item.value ?? item.id ?? index;
  }

  /**
   * Récupère une configuration de rangée par son rowIndex
   */
  getRowConfigByIndex(rowIndex: number) {
    return this.configuration?.rowConfigurations.find(r => r.rowIndex === rowIndex);
  }

  /**
   * Retourne le nombre total d'options de type de vasque
   * Utilisé pour masquer la section si une seule option existe (pas seulement si une seule est available)
   */
  getBasinTypesCount(): number {
    return this.configuration?.availableOptions?.basinTypes?.length ?? 0;
  }

  /**
   * Retourne le nombre de styles de vasque autorisés par la composition (via allowedBasinTypes)
   * Utilisé pour masquer la section si un seul style est autorisé
   */
  getAvailableBasinStylesCount(): number {
    return this.configuration?.availableOptions?.basinStyles?.length ?? 0;
  }

  // ============================================
  // Alerte dépassement largeur max filtrée
  // ============================================

  get maxFurnitureWidth(): number | null {
    return this.configurationService.maxFurnitureWidth;
  }

  isNiche(colConfig: ColumnConfiguration): boolean {
    return colConfig.currentProduct?.attribute === ProductAttribute.Niche;
  }

  // ============================================
  // Gestion des positions de niches
  // ============================================

  getNicheInfos(): Array<{ rowIndex: number; columnIndex: number; isOnLeft: boolean; label: string }> {
    const rows = this.configuration?.composition?.compositionConfiguration?.rows ?? [];
    const niches: Array<{ rowIndex: number; columnIndex: number; isOnLeft: boolean; label: string }> = [];

    for (const row of [...rows].sort((a, b) => a.index - b.index)) {
      const sortedCols = [...row.columns].sort((a, b) => a.index - b.index);
      for (const col of sortedCols) {
        if (col.product?.attribute === ProductAttribute.Niche) {
          const rowLabel = row.index === 0 ? 'haut' : 'bas';
          niches.push({
            rowIndex: row.index,
            columnIndex: col.index,
            isOnLeft: col.index === 0,
            label: `Niche du ${rowLabel}`,
          });
        }
      }
    }

    return niches;
  }

  hasNiches(): boolean {
    return this.getNicheInfos().length > 0;
  }

  onNichePositionToggle(rowIndex: number, columnIndex: number): void {
    this.configurationService.swapNichePosition(rowIndex, columnIndex);
  }

  getSortedRowConfigurations(): RowConfiguration[] {
    return [...this.configuration.rowConfigurations]
      .sort((a, b) => a.rowIndex - b.rowIndex)
      .map(row => ({
        ...row,
        columnConfigurations: [...row.columnConfigurations].sort((a, b) => a.columnIndex - b.columnIndex),
      }));
  }

  isWidthExceedingFilter(width: number | undefined): boolean {
    if (!width || !this.maxFurnitureWidth) return false;
    return width > this.maxFurnitureWidth;
  }
}
